import * as members from '../../privates/privateMembers';
import {validate} from '../../privates/validations/validation';

/**
 * @mixin ViewportMixin
 * @summary Provides functionality for elements that can be scrolled into and out
 *  of view.
 * @memberof $w
 */
class ViewportMixin {

    /**
     * @function onViewportEnter
     * @syntax
     * function onViewportEnter(handler: EventHandler): Element
     * callback EventHandler(event: Event, $w: Function): void
     * @summary Adds an event handler that runs when an element is displayed
     *  in the viewable part of the current window.
     * @description
     *  An element enters the viewport when the page is scrolled to show any
     *  part of the element. An element also enters the viewport if it was
     *  [hidden]($w.HiddenMixin.html#hidden) or [collapsed]($w.CollapsedMixin.html#collapsed)
     *  and is then shown or expanded in the viewable part of the current window. `onViewportEnter()` 
     *  is not fired for [hidden]($w.HiddenMixin.html#hidden) or [collapsed]($w.CollapsedMixin.html#collapsed) 
     *  elements even if they are scrolled into view.
     * @note
     *  **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  <a href="https://www.wix.com/code/home/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers" target="_blank">here</a>.
     * @param {$w.EventHandler} handler The name of the function or
     *  the function expression to run when the element enters the viewport.
     * @returns {$w.Element} The element on which the event is now registered.
     * @snippet [ViewportMixin-onViewportEnter.es6=Get the ID of the element that has entered the viewport]
     * @see [onViewportLeave( )](#onViewportLeave)
     * @memberof $w.ViewportMixin
     * @eventtype viewportEnter
     * @eventhandler
     */
    onViewportEnter(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onViewportEnter',
            dataToValidate: {onViewportEnter: cb},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().VIEWPORT_ENTER, controllerId, cb);
        return this;
    }

    /**
     * @function onViewportLeave
     * @syntax
     * function onViewportLeave(handler: EventHandler): Element
     * callback EventHandler(event: Event, $w: Function): void
     * @summary Adds an event handler that runs when an element is no longer
     *  displayed in the viewable part of the current window.
     * @description
     *  An element leaves the viewport when the page is scrolled so that the
     *  element is completely out of view. An element also leaves the viewport
     *  if it was shown or expanded and is then [hidden]($w.HiddenMixin.html#hidden)
     *  or [collapsed]($w.CollapsedMixin.html#collapsed) from the viewable part
     *  of the current window.  `onViewportLeave()` 
     *  is not fired for [hidden]($w.HiddenMixin.html#hidden) or [collapsed]($w.CollapsedMixin.html#collapsed) 
     *  elements even if they are scrolled out of view.
     * @note
     *  **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  <a href="https://www.wix.com/code/home/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers" target="_blank">here</a>.
     * @param {$w.EventHandler} handler The name of the function or
     *  the function expression to run when the element leaves the viewport.
     * @returns {$w.Element} The element on which the event is now registered.
     * @snippet [ViewportMixin-onViewportLeave.es6=Get the ID of the element that has left the viewport]
     * @see [onViewportEnter( )](#onViewportEnter)
     * @memberof $w.ViewportMixin
     * @eventtype viewportLeave
     * @eventhandler
     */
    onViewportLeave(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onViewportLeave',
            dataToValidate: {onViewportLeave: cb},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().VIEWPORT_LEAVE, controllerId, cb);
        return this;
    }
}

export default ViewportMixin;
